import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FaBuilding,
  FaClipboardList,
  FaCalculator,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaComments,
  FaFileAlt,
  FaFolderOpen,
  FaHome,
  FaLink,
  FaMoon,
  FaPlus,
  FaSun,
  FaShieldAlt,
  FaUserCircle,
} from "react-icons/fa";
import {
  THEMES,
  getTheme,
  toggleTheme,
  onThemeChange,
  offThemeChange,
} from "../theme/theme";

const BASE_URL = "https://case-tracking-backend.onrender.com";
const NAVBAR_REFRESH_MS = 8000;
const AUTH_USER_UPDATED_EVENT = "auth:user-updated";
const AUTH_USER_CLEARED_EVENT = "auth:user-cleared";

function trimPreview(value) {
  if (!value) return "You have a new message.";
  return value.length > 90 ? `${value.slice(0, 90)}…` : value;
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function getDisplayName(user) {
  const name =
    user?.username ||
    user?.name ||
    user?.fullName ||
    user?.displayName ||
    user?.email?.split("@")[0] ||
    "User";

  return String(name).trim() || "User";
}

function getDisplayRole(user) {
  if (user?.position) return user.position;
  if (user?.role) return user.role;
  if (user?.isAdmin) return "Administrator";
  return "Conveyancer";
}

function persistCurrentUser(user) {
  try {
    if (user && typeof user === "object") {
      localStorage.setItem("user", JSON.stringify(user));
      window.dispatchEvent(new CustomEvent(AUTH_USER_UPDATED_EVENT, { detail: { user } }));
    }
  } catch {}
}

function clearCurrentUser() {
  try {
    localStorage.removeItem("user");
    window.dispatchEvent(new Event(AUTH_USER_CLEARED_EVENT));
  } catch {}
}

function BrandMark({ compact = false }) {
  return (
    <span className={compact ? "gba-brand-mark compact" : "gba-brand-mark"} role="img" aria-label="Gerhard Barnard Inc">
      <span>G</span>
      <span>B</span>
    </span>
  );
}

function initialsFromName(name) {
  const cleaned = String(name || "User").trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export default function Navbar() {
  const [mode, setMode] = useState(getTheme());
  const [isAuthed, setIsAuthed] = useState(!!localStorage.getItem("token"));
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, setToast] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("gbaSidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });
  const location = useLocation();

  const previousUnreadMapRef = useRef({});
  const initializedUnreadRef = useRef(false);
  const audioContextRef = useRef(null);
  const originalTitleRef = useRef(
    typeof document !== "undefined" && document.title
      ? document.title
      : "Conveyancing Portal"
  );
  const activeConversationUserIdRef = useRef("");

  const publicRoute = ["/login", "/register", "/logout"].includes(location.pathname) || location.pathname.startsWith("/portal/") || location.pathname.startsWith("/fica/");
  const displayName = getDisplayName(currentUser);
  const displayRole = getDisplayRole(currentUser);
  const isCalculator = location.pathname.startsWith("/calculator");
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  const usesWorkspaceSidebar =
    isDashboard ||
    location.pathname.startsWith("/my-transactions") ||
    location.pathname.startsWith("/accounts") ||
    location.pathname.startsWith("/document") ||
    location.pathname.startsWith("/documents");

  const playNotificationSound = useCallback(() => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass();
      }

      const ctx = audioContextRef.current;

      const playTone = () => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(880, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);

        gainNode.gain.setValueAtTime(0.0001, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.09, ctx.currentTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.32);
      };

      if (ctx.state === "suspended") {
        ctx.resume().then(playTone).catch(() => {});
      } else {
        playTone();
      }
    } catch (err) {
      console.error("Notification sound failed:", err);
    }
  }, []);

  const showBrowserNotification = useCallback(async (title, body, options = {}) => {
    try {
      if (typeof window === "undefined") return;
      if (!("Notification" in window)) return;
      if (Notification.permission !== "granted") return;

      const {
        tag = "personal-message-alert",
        userId = "",
        url = `${window.location.origin}/messages`,
      } = options;

      const notificationOptions = {
        body,
        tag,
        renotify: true,
        requireInteraction: true,
        icon: "/logo192.png",
        badge: "/logo192.png",
        data: {
          url,
          userId,
          timestamp: Date.now(),
        },
      };

      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          if (registration?.showNotification) {
            await registration.showNotification(title, notificationOptions);
            return;
          }
        } catch (swError) {
          console.error("Service worker notification failed:", swError);
        }
      }

      new Notification(title, notificationOptions);
    } catch (err) {
      console.error("Browser notification failed:", err);
    }
  }, []);

  const fetchUnreadState = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      setUnreadCount(0);
      setToast(null);
      previousUnreadMapRef.current = {};
      initializedUnreadRef.current = false;
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/personal-messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const safeConversations = Array.isArray(res.data) ? res.data : [];

      let totalUnread = 0;
      const nextUnreadMap = {};
      let newestConversationWithNewUnread = null;

      for (const conversation of safeConversations) {
        const userId = conversation?.user?._id;
        if (!userId) continue;

        const currentUnread = Number(conversation?.unreadCount || 0);
        const previousUnread = Number(previousUnreadMapRef.current[userId] || 0);

        totalUnread += currentUnread;
        nextUnreadMap[userId] = currentUnread;

        const isActiveConversation =
          activeConversationUserIdRef.current &&
          activeConversationUserIdRef.current === userId;

        if (
          initializedUnreadRef.current &&
          currentUnread > previousUnread &&
          !isActiveConversation
        ) {
          if (
            !newestConversationWithNewUnread ||
            new Date(conversation.updatedAt) >
              new Date(newestConversationWithNewUnread.updatedAt)
          ) {
            newestConversationWithNewUnread = conversation;
          }
        }
      }

      setUnreadCount(totalUnread);

      if (newestConversationWithNewUnread) {
        const username =
          newestConversationWithNewUnread?.user?.username || "Someone";
        const preview = trimPreview(newestConversationWithNewUnread?.lastMessage);
        const userId = newestConversationWithNewUnread?.user?._id || "general";

        setToast({
          id: Date.now(),
          title: `New message from ${username}`,
          body: preview,
        });

        playNotificationSound();
        showBrowserNotification(`New message from ${username}`, preview, {
          tag: `personal-message-alert-${userId}`,
          userId,
        });
      }

      previousUnreadMapRef.current = nextUnreadMap;
      initializedUnreadRef.current = true;
    } catch (err) {
      console.error("Failed to fetch unread state:", err);
      setUnreadCount(0);
    }
  }, [playNotificationSound, showBrowserNotification]);


  useEffect(() => {
    const syncStoredUser = () => setCurrentUser(getStoredUser());

    window.addEventListener(AUTH_USER_UPDATED_EVENT, syncStoredUser);
    window.addEventListener(AUTH_USER_CLEARED_EVENT, syncStoredUser);
    window.addEventListener("storage", syncStoredUser);

    return () => {
      window.removeEventListener(AUTH_USER_UPDATED_EVENT, syncStoredUser);
      window.removeEventListener(AUTH_USER_CLEARED_EVENT, syncStoredUser);
      window.removeEventListener("storage", syncStoredUser);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setCurrentUser(null);
      clearCurrentUser();
      return undefined;
    }

    let cancelled = false;
    const stored = getStoredUser();
    if (stored) setCurrentUser(stored);

    axios
      .get(`${BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        const nextUser = res.data || null;
        setCurrentUser(nextUser);
        if (nextUser) persistCurrentUser(nextUser);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to refresh signed-in user:", err);
        if (!stored) setCurrentUser(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthed, location.pathname]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.toggle("gba-sidebar-collapsed", sidebarCollapsed);
    try {
      localStorage.setItem("gbaSidebarCollapsed", sidebarCollapsed ? "true" : "false");
    } catch {}

    return () => {
      document.body.classList.remove("gba-sidebar-collapsed");
    };
  }, [sidebarCollapsed]);

  useEffect(() => {
    const handler = (e) => setMode(e.detail?.mode || getTheme());
    onThemeChange(handler);
    return () => offThemeChange(handler);
  }, []);

  useEffect(() => {
    const authed = !!localStorage.getItem("token");
    setIsAuthed(authed);

    if (!authed) {
      setUnreadCount(0);
      setToast(null);
      previousUnreadMapRef.current = {};
      initializedUnreadRef.current = false;
      return;
    }

    fetchUnreadState();
  }, [location, fetchUnreadState]);

  useEffect(() => {
    if (!isAuthed) return undefined;

    const interval = setInterval(fetchUnreadState, NAVBAR_REFRESH_MS);
    return () => clearInterval(interval);
  }, [isAuthed, fetchUnreadState]);

  useEffect(() => {
    const handleRefresh = () => {
      fetchUnreadState();
    };

    window.addEventListener("messages:refresh-unread", handleRefresh);
    window.addEventListener("focus", handleRefresh);

    return () => {
      window.removeEventListener("messages:refresh-unread", handleRefresh);
      window.removeEventListener("focus", handleRefresh);
    };
  }, [fetchUnreadState]);

  useEffect(() => {
    if (!isAuthed) return undefined;
    if (!("Notification" in window)) return undefined;
    if (Notification.permission !== "default") return undefined;

    let asked = false;

    const requestPermission = () => {
      if (asked) return;
      asked = true;
      Notification.requestPermission().catch(() => {});
    };

    window.addEventListener("click", requestPermission, { once: true });
    window.addEventListener("keydown", requestPermission, { once: true });
    window.addEventListener("touchstart", requestPermission, { once: true });

    return () => {
      window.removeEventListener("click", requestPermission);
      window.removeEventListener("keydown", requestPermission);
      window.removeEventListener("touchstart", requestPermission);
    };
  }, [isAuthed]);

  useEffect(() => {
    const handleActiveConversationChange = (event) => {
      activeConversationUserIdRef.current = event?.detail?.userId || "";
    };

    const handleLiveIncoming = (event) => {
      const username = event?.detail?.username || "Someone";
      const preview = trimPreview(event?.detail?.body);
      const userId = event?.detail?.userId || "live";

      setToast({
        id: Date.now(),
        title: `New message from ${username}`,
        body: preview,
      });

      playNotificationSound();
      showBrowserNotification(`New message from ${username}`, preview, {
        tag: `personal-message-alert-${userId}`,
        userId,
      });
    };

    window.addEventListener(
      "messages:active-conversation-change",
      handleActiveConversationChange
    );
    window.addEventListener("messages:incoming-live", handleLiveIncoming);

    return () => {
      window.removeEventListener(
        "messages:active-conversation-change",
        handleActiveConversationChange
      );
      window.removeEventListener("messages:incoming-live", handleLiveIncoming);
    };
  }, [playNotificationSound, showBrowserNotification]);

  useEffect(() => {
    const baseTitle = originalTitleRef.current || "Conveyancing Portal";
    document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;

    return () => {
      document.title = baseTitle;
    };
  }, [unreadCount]);

  if (publicRoute || !isAuthed) return null;

  const navClass = ({ isActive }) => (isActive ? "gba-nav-link active" : "gba-nav-link");
  const dashboardClass = location.pathname === "/" || location.pathname === "/dashboard"
    ? "gba-nav-link active"
    : "gba-nav-link";
  const accountsClass = location.pathname.startsWith("/accounts")
    ? "gba-nav-link active"
    : "gba-nav-link";
  const documentsClass = location.pathname.startsWith("/document") || location.pathname.startsWith("/documents")
    ? "gba-nav-link active"
    : "gba-nav-link";

  return (
    <>
      <aside className={sidebarCollapsed ? "gba-sidebar collapsed" : "gba-sidebar"} aria-label="Portal sidebar">
        <div className="gba-sidebar-brand">
          <BrandMark />
          <div className="gba-sidebar-brand-copy">
            <strong>Gerhard Barnard Inc</strong>
            <span>Conveyancing Portal</span>
          </div>
          <button
            type="button"
            className="gba-sidebar-collapse"
            onClick={() => setSidebarCollapsed((value) => !value)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        </div>

        <div className="gba-sidebar-context">
          {isCalculator ? (
            <>
              <NavLink to="/dashboard" className="gba-sidebar-back">
                ‹ Cost Calculator
              </NavLink>
              <div className="gba-stepper" aria-label="Calculator flow">
                <div className="gba-step active">
                  <span>1</span>
                  <div>
                    <strong>Matter Details</strong>
                    <small>Property & purchase info</small>
                  </div>
                </div>
                <div className="gba-step">
                  <span>2</span>
                  <div>
                    <strong>Allowances</strong>
                    <small>Firm & bond allowances</small>
                  </div>
                </div>
                <div className="gba-step">
                  <span>3</span>
                  <div>
                    <strong>Review & Breakdown</strong>
                    <small>Calculation summary</small>
                  </div>
                </div>
              </div>
            </>
          ) : usesWorkspaceSidebar ? (
            <div id="gba-sidebar-dynamic-slot" className="gba-sidebar-dynamic-slot" />
          ) : (
            <div id="gba-sidebar-dynamic-slot" className="gba-sidebar-dynamic-slot" />
          )}
        </div>

        <div className="gba-sidebar-footer">
          <BrandMark compact />
          <p>Trusted expertise.<br />Seamless transfers.</p>
        </div>
      </aside>

      <header className="gba-topbar">
        <div className="gba-nav-stack">
          <nav className="gba-nav-links gba-nav-primary-row" aria-label="Primary navigation">
            <NavLink to="/dashboard" className={dashboardClass}>
              <FaHome /> <span>Dashboard</span>
            </NavLink>
            <NavLink to="/my-transactions" className={navClass}>
              <FaFolderOpen /> <span>My Transactions</span>
            </NavLink>
            <NavLink to="/messages" className={navClass}>
              <span className="gba-link-with-badge">
                <FaComments /> <span>Messages</span>
                {unreadCount > 0 && (
                  <span className="gba-message-badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </span>
            </NavLink>
            <NavLink to="/accounts-hub" className={accountsClass}>
              <FaFileAlt /> <span>Accounts Hub</span>
            </NavLink>
            <NavLink to="/report-centre" className={navClass}>
              <FaFileAlt /> <span>Report Centre</span>
            </NavLink>
            <NavLink to="/portal-centre" className={navClass}>
              <FaLink /> <span>Portals</span>
            </NavLink>
          </nav>

          <nav className="gba-nav-links gba-nav-secondary-row" aria-label="More navigation">
            <NavLink to="/calculator" className={navClass}>
              <FaCalculator /> <span>Cost Calculator</span>
            </NavLink>
            <NavLink to="/inhouse-agents" className={navClass}>
              <FaBuilding /> <span>Inhouse Agents</span>
            </NavLink>
            <NavLink to="/document-centre" className={documentsClass}>
              <FaClipboardList /> <span>Document Centre</span>
            </NavLink>
            <NavLink to="/fica-hub" className={navClass}>
              <FaShieldAlt /> <span>FICA Hub</span>
            </NavLink>
          </nav>
        </div>

        <div className="gba-topbar-actions">
          <button
            type="button"
            className="gba-icon-toggle"
            onClick={() => {
              toggleTheme();
              setMode(getTheme() === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT);
            }}
            aria-pressed={mode === THEMES.DARK}
            title="Toggle theme"
          >
            <FaSun />
            <span className={mode === THEMES.DARK ? "active" : ""}>
              <FaMoon />
            </span>
          </button>

          <NavLink
            to="/case/new"
            className="gba-new-transaction"
            title="Create a new transaction"
            aria-label="Create a new transaction"
          >
            <FaPlus /> New Transaction
          </NavLink>

          <details className="gba-user-menu">
            <summary>
              <span className="gba-user-avatar">{initialsFromName(displayName)}</span>
              <span className="gba-user-copy">
                <strong>{displayName}</strong>
                <small>{displayRole}</small>
              </span>
              <FaChevronDown />
            </summary>
            <div className="gba-user-dropdown">
              <div className="gba-user-dropdown-head">
                <FaUserCircle />
                <span>{displayName}</span>
              </div>
              <NavLink to="/logout">Log out</NavLink>
            </div>
          </details>
        </div>
      </header>

      {toast && (
        <div className="gba-toast-wrap">
          <div className="gba-toast-header">
            <div className="gba-toast-title">{toast.title}</div>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="gba-toast-close"
              aria-label="Close notification"
              title="Close notification"
            >
              ×
            </button>
          </div>
          <div className="gba-toast-body">{toast.body}</div>
        </div>
      )}
    </>
  );
}
